import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchPublicBuddy } from "../lib/api";
import { Sparkles, Wand2, ArrowRight, Loader2, Heart, ShieldCheck } from "lucide-react";
import useSeo from "../lib/useSeo";
import { trackOnce } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public share page for a custom Buddy — `/buddies/:id/share`.
 *
 * This is the destination for every "Share your Buddy" link. Renders a warm,
 * shareable landing that:
 *   1. Shows off the Buddy someone made (image, name, food, tagline)
 *   2. Explains what Plate Buddies is in 2 sentences
 *   3. Drives the visitor to make their own free — the viral loop
 *
 * SEO: dynamic per-buddy title + description + OG image so link previews on
 * iMessage/WhatsApp/Facebook actually show the buddy. Falls back gracefully
 * when the buddy is 404 / hidden.
 */
export default function BuddyShare() {
  const { id } = useParams();
  const [buddy, setBuddy] = useState(null);
  const [status, setStatus] = useState("loading"); // "loading" | "ok" | "not_found"

  useEffect(() => {
    trackOnce("share_page_view", { buddy_id: id });
    let cancelled = false;
    fetchPublicBuddy(id)
      .then((b) => {
        if (cancelled) return;
        setBuddy(b);
        setStatus("ok");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("not_found");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const imageAbsolute = buddy?.image_url
    ? `${BACKEND_URL}${buddy.image_url}`
    : undefined;
  const foodLine = buddy?.display_food ? ` — a friendly ${String(buddy.display_food).toLowerCase()} character` : "";

  useSeo({
    title: buddy?.name
      ? `Meet ${buddy.name} — a Plate Buddy | Design your own free`
      : "Meet this Plate Buddy | Design your own free",
    description: buddy?.name
      ? `${buddy.name}${foodLine} — designed on Plate Buddies. Make your kid their very own food character in 30 seconds, free.`
      : "A custom food character made on Plate Buddies. Design your own free — kid-friendly recipes included.",
    image: imageAbsolute,
    url: `https://platebuddies.com/buddies/${id}/share`,
    type: "article",
  });

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-pb-cream flex items-center justify-center">
        <Loader2 className="h-10 w-10 text-pb-coral animate-spin" />
      </div>
    );
  }

  if (status === "not_found") {
    return (
      <div className="min-h-screen bg-pb-cream flex items-center justify-center px-6 text-center">
        <div className="max-w-md">
          <div className="mx-auto h-14 w-14 rounded-full bg-pb-cream flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-pb-coral" />
          </div>
          <h1 className="font-display font-black text-3xl mt-5 text-pb-ink">
            This Buddy has wandered off
          </h1>
          <p className="mt-2 text-pb-soft">
            The Buddy you're looking for may have been removed. But you can design your own in 30 seconds — free.
          </p>
          <Link
            to="/free-buddy"
            data-testid="btn-share-not-found-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-7 py-4 shadow-lg hover:scale-105 transition"
          >
            <Wand2 className="h-4 w-4" /> Design my own Buddy
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="page-buddy-share"
      className="font-body min-h-screen"
      style={{ background: buddy?.bg || "#FFF6D6" }}
    >
      {/* Minimal header — one-goal page, no full nav so nothing distracts from "make your own" */}
      <header className="max-w-3xl mx-auto px-5 sm:px-8 pt-6 flex items-center justify-between">
        <Link
          to="/"
          data-testid="share-logo"
          className="font-display font-black text-xl text-pb-ink"
        >
          Plate<span className="text-pb-coral">Buddies</span>
        </Link>
        <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/60 backdrop-blur px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest text-pb-ink">
          <Heart className="h-3 w-3 text-pb-coral fill-pb-coral" /> Someone shared this
        </span>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 pb-16 text-center">
        <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">
          Meet
        </p>
        <h1
          data-testid="share-buddy-name"
          className="font-display font-black tracking-tight text-pb-ink mt-2 text-5xl md:text-6xl leading-[0.95]"
        >
          {buddy.name}
        </h1>
        {buddy.tagline && (
          <p
            data-testid="share-buddy-tagline"
            className="mt-3 text-sm font-extrabold uppercase tracking-widest"
            style={{ color: buddy.color }}
          >
            {buddy.tagline}
          </p>
        )}

        <div className="mt-8 relative inline-block">
          <div className="rounded-[2.5rem] overflow-hidden border-[8px] border-white shadow-[0_30px_80px_rgba(28,22,40,0.25)] rotate-[-1.5deg] max-w-md mx-auto">
            {imageAbsolute && (
              <img
                data-testid="share-buddy-image"
                src={imageAbsolute}
                alt={buddy.name}
                className="w-full h-auto"
              />
            )}
          </div>
          <span
            className="absolute -top-3 -right-3 sm:-right-8 bg-white rounded-full px-3 py-1.5 shadow-lg rotate-3 text-[10px] font-black uppercase tracking-widest"
            style={{ color: buddy.color }}
          >
            One of a kind
          </span>
        </div>

        {(buddy.display_food || buddy.recipe_title) && (
          <p className="mt-8 text-lg text-pb-ink/80 max-w-xl mx-auto leading-relaxed">
            {buddy.display_food && (
              <>
                <b className="text-pb-ink">{buddy.name}</b> is a{" "}
                <span className="font-extrabold" style={{ color: buddy.color }}>
                  {String(buddy.display_food).toLowerCase()}
                </span>{" "}
                character
              </>
            )}
            {buddy.recipe_title && (
              <>
                {buddy.display_food ? " paired with " : "Paired with "}
                <b>{buddy.recipe_title}</b>
              </>
            )}
            {"."}
          </p>
        )}

        {/* The viral hook — design your own */}
        <div className="mt-10 bg-white rounded-[2.5rem] p-8 sm:p-10 border border-white shadow-[0_20px_50px_rgba(28,22,40,0.08)] text-left max-w-2xl mx-auto">
          <div className="flex items-center gap-2 text-pb-coral">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs font-extrabold uppercase tracking-widest">
              Now design one for your kid
            </span>
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl mt-3 leading-tight text-pb-ink">
            Your kid&rsquo;s very own food character — free.
          </h2>
          <p className="mt-3 text-pb-soft leading-relaxed">
            Plate Buddies turns dinner into a story. Your child names + designs a one-of-a-kind Plate Buddy in 30 seconds — we illustrate it and pair it with a kid-friendly recipe. Ages 2–7. No credit card needed.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link
              to="/free-buddy"
              data-testid="btn-share-page-cta"
              className="inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-7 py-4 shadow-[0_14px_32px_rgba(242,106,52,0.45)] hover:scale-105 transition"
            >
              <Wand2 className="h-5 w-5" /> Make my Buddy — Free
              <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="text-xs text-pb-soft font-bold inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-pb-aqua" /> No credit card · 30 seconds
            </p>
          </div>
        </div>

        <p className="mt-10 text-xs text-pb-soft">
          Made by a real family with Plate Buddies · <Link to="/" className="underline font-bold">platebuddies.com</Link>
        </p>
      </main>
    </div>
  );
}
